/**
 * Recherche globale.
 *
 * Une seule barre interroge les cinq collections qu'on cherche par leur nom :
 * clients, employés, devis, factures, chantiers. Rien n'est indexé — les
 * collections sont déjà en mémoire, et une entreprise qui tient dans un
 * téléphone n'a pas de quoi justifier un index.
 */

/** En deçà, la recherche rendrait la moitié du fichier. */
export const LETTRES_MINIMUM = 2;

/** Compare sans se soucier de la casse ni des accents. */
const contient = (texte, q) =>
  String(texte || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .includes(q);

/**
 * Cherche un terme dans toutes les collections.
 *
 * Les pièces commerciales se cherchent aussi bien par leur numéro que par le
 * nom du client : c'est l'un ou l'autre qu'on a sous les yeux au moment où on
 * les cherche, rarement les deux.
 *
 * Au magasin, les chantiers ne se cherchent pas : ils sont du bâtiment, et
 * le résultat mènerait à un écran fermé. Une société Mix les retrouve dans
 * l'univers Bâtiment.
 *
 * @param {string} terme ce que l'utilisateur a saisi
 * @param {object} donnees collections à parcourir
 * @param {boolean} [donnees.mag] vrai dans l'univers Magasin
 * @param {string} [donnees.motPiece] nom de la pièce commerciale (« Offre » ou « Devis »)
 * @returns {Array<{type: string, nom: string, sub: string, nav: string, ouverture: object}>}
 *   `ouverture` désigne l'élément à ouvrir sur l'écran `nav`, et non sa liste
 */
export function cherche(
  terme,
  { clients, employes, devis, factures, chantiers, mag, motPiece = 'Devis' } = {},
) {
  const q = String(terme || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
  if (q.length < LETTRES_MINIMUM) {
    return [];
  }

  const res = [];
  const nomDuClient = (id) => (clients || []).find((c) => c.id === id)?.nom || '';

  (clients || [])
    .filter((c) => contient(c.nom, q))
    .forEach((c) =>
      res.push({
        type: '👤 Client',
        nom: c.nom,
        sub: c.localite || c.npa || '',
        nav: 'clients',
        ouverture: { clientId: c.id },
      }),
    );

  (employes || [])
    .filter((e) => contient((e.prenom || '') + ' ' + (e.nom || ''), q))
    .forEach((e) =>
      res.push({
        type: '👷 Employé',
        nom: ((e.prenom || '') + ' ' + (e.nom || '')).trim(),
        sub: 'contrat · fiches de paie · certificats',
        nav: 'equipe',
        ouverture: { empId: e.id },
      }),
    );

  const piece = (liste, type, nav, defaut, cle) =>
    (liste || [])
      .filter((p) => contient(nomDuClient(p.clientId), q) || contient(p.num, q))
      .forEach((p) => {
        const cl = nomDuClient(p.clientId);
        res.push({
          type,
          nom: (p.num || defaut) + (cl ? ' · ' + cl : ''),
          sub: p.date || '',
          nav,
          ouverture: { [cle]: p.id },
        });
      });

  piece(devis, '📝 ' + motPiece, 'devis', motPiece, 'devisId');
  piece(factures, '🧾 Facture', 'factures', 'Facture', 'factureId');

  (mag ? [] : chantiers || [])
    .filter((c) => contient(c.nom, q))
    .forEach((c) =>
      res.push({
        type: '🏗️ Chantier',
        nom: c.nom,
        sub: c.statut || '',
        nav: 'chantiers',
        ouverture: { chantierId: c.id },
      }),
    );

  return res;
}
